import gsap from 'gsap'
import './Detail.css'
import { splitReveal, splitConceal, resplitLines, SplitFx } from '../../utils/text.js'
import { attachHoverUnderline, UnderlineFx } from '../../utils/underline.js'
import { DURATION, EASE, STAGGER } from '../../../shared/motion.js'
import { projects } from '../../../content/projects.js'
import { device } from '../../../shared/device.js'

const LABELS = {
    link: 'View Site',
    next: 'NEXT PROJECT',
    fields: ['Project', 'Description', 'Year', 'Role']
}

const PROGRESS_COMPLETE_THRESHOLD = 0.999
// 真實滑動 (尤其 Lenis 阻尼/慣性) 停下來時，animatedScroll 常會離 limit 差個 1~2px 才靜止，
// 導致 ratio 版門檻 (PROGRESS_COMPLETE_THRESHOLD) 永遠到不了 0.999——視覺上底線已經填滿，
// 但 _nextComplete 其實還是 false，點擊沒反應。改用絕對像素差判斷「已經到底」比較穩
const COMPLETE_EPSILON_PX = 2
const GALLERY_REVEAL_OFFSET = 32
// 純防呆用的逾時保底（避免圖片真的載入失敗又沒觸發 error 事件時卡住整個轉場），
// 不是正常路徑的等待上限——設太短反而會在圖片還沒真的 ready 前就搶先播放動畫，等於沒等
const IMAGE_READY_TIMEOUT = 4000

class AppDetail extends HTMLElement {
    constructor() {
        super()
        this.project = null
        this._director = null

        this._contentSplit = null
        this._nextSplit = null
        this._contentAnimation = null
        this._nextComplete = false
        this._nextEnabled = false
        this._nextProject = null

        // RWD resize 造成斷行改變時，是否有一次重建 split 結構的請求還沒處理（動畫進行中會延後到播完再補做）
        this._pendingResize = false

        this._heroEl = null
        this._images = []
        this._galleryCount = 0
        this._galleryIndex = -1
        this._lenis = null
        this._revealToken = 0
    }

    setDirector(director) {
        this._director = director
    }

    /** 由 Producer 在取得本元件 DOM 節點後注入：.detail__gallery 這個容器本身不會隨路由重建，
     *  只有裡面的圖片會換 (見 setImages)，所以 Lenis 實例終生只建一次，不必自己 new */
    setLenis(lenis) {
        this._lenis = lenis
        this._lenis.stop()
    }

    /** 供 Producer 建立綁定本元件實際可捲動內容的 Lenis 用 */
    getGalleryEl() {
        return this.galleryEl
    }

    /** 供 Producer 建立綁定本元件實際可捲動內容的 Lenis 用 */
    getGalleryTrackEl() {
        return this.galleryTrackEl
    }

    connectedCallback() {
        this._renderDOM()
        this._setupDOMReferences()
        this._setupEvents()
        this._setInteractive(false)
        this._syncMobileLayout()
    }

    _renderDOM() {
        this.innerHTML = /* html */ `
            <aside class="detail__aside">
                <div class="detail_infos">
                    <div class="detail__field detail__field--project">
                        <span class="detail__field-label"></span>
                        <h1 class="detail__title"></h1>
                    </div>

                    <div class="detail__field detail__field--desc">
                        <span class="detail__field-label"></span>
                        <p class="detail__desc"></p>
                    </div>

                    <dl class="detail__field detail__field--year">
                        <dt class="detail__field-label"></dt>
                        <dd class="detail__value detail__year"></dd>
                    </dl>

                    <dl class="detail__field detail__field--role">
                        <dt class="detail__field-label"></dt>
                        <dd class="detail__value detail__role"></dd>
                    </dl>

                    <button class="detail__link" type="button" aria-label="View Site"><span class="detail__link-label"></span></button>
                </div>

                <div class="detail_bottom">
                    <span class="counter"></span>
                    <button class="detail__next" type="button">
                        <span class="detail__next-label-wrap">
                            <span class="detail__next-label"></span>
                            <span class="detail__next-label-overlay"></span>
                        </span>
                    </button>
                </div>
            </aside>
            <div class="detail__gallery">
                <div class="detail__gallery-target"></div>
                <div class="detail__gallery-track"></div>
            </div>
            `
    }

    _setupDOMReferences() {
        this.asideEl = this.querySelector('.detail__aside')
        this.bottomEl = this.querySelector('.detail_bottom')
        this.titleEl = this.querySelector('.detail__title')
        this.descEl = this.querySelector('.detail__desc')
        this.roleEl = this.querySelector('.detail__role')
        this.yearEl = this.querySelector('.detail__year')
        this.link = this.querySelector('.detail__link')
        this.linkLabelEl = this.querySelector('.detail__link-label')

        this.nextEl = this.querySelector('.detail__next')
        this.counterEl = this.querySelector('.counter')
        this.nextWrapEl = this.querySelector('.detail__next-label-wrap')
        this.nextLabelEl = this.querySelector('.detail__next-label')
        this.nextOverlayEl = this.querySelector('.detail__next-label-overlay')

        this.fieldLabelEls = [...this.querySelectorAll('.detail__field-label')]

        this.galleryEl = this.querySelector('.detail__gallery')
        this.galleryTargetEl = this.querySelector('.detail__gallery-target')
        this.galleryTrackEl = this.querySelector('.detail__gallery-track')

        this.nextUnderline = new UnderlineFx(this.nextWrapEl)
        this.counterFx = new SplitFx(this.counterEl)
    }

    _setupEvents() {
        attachHoverUnderline(this.link)

        this.link.addEventListener('click', () => {
            if (this.project?.url) {
                window.open(this.project.url, '_blank', 'noopener')
            }
        })

        this.nextEl.addEventListener('mouseenter', () => {
            return device.hasHover && this._nextComplete && this.nextUnderline.drawIn()
        })
        this.nextEl.addEventListener('mouseleave', () => {
            return device.hasHover && this._nextComplete && this.nextUnderline.erase()
        })
        this.nextEl.addEventListener('click', () => {
            if (this._nextComplete && this._nextProject) {
                this._director?.requestNavigate(`/project/${this._nextProject.slug}`)
            }
        })
    }

    _setInteractive(active) {
        const value = active ? 'auto' : 'none'
        this.link.style.pointerEvents = value
        this.nextEl.style.pointerEvents = value
        this.galleryEl.style.pointerEvents = value
    }

    _updateDOMContent(project) {
        this.project = project
        this._nextProject = this._resolveNextProject(project)
        this._nextEnabled = !!this._nextProject?.enable

        this.fieldLabelEls.forEach((el, i) => {
            el.textContent = project ? LABELS.fields[i] : ''
        })

        this.titleEl.textContent = project?.title || ''
        this.descEl.textContent = project?.description || ''
        this.roleEl.textContent = project?.role || ''
        this.yearEl.textContent = project?.year || ''
        this.linkLabelEl.textContent = project?.url ? LABELS.link : ''
        this.counterEl.textContent = project ? '01' : ''

        const nextText = project ? LABELS.next : ''
        this.nextLabelEl.textContent = nextText
        this.nextOverlayEl.textContent = nextText
    }

    _resolveNextProject(project) {
        const idx = project ? projects.indexOf(project) : -1
        if (idx < 0) {
            return null
        }
        return projects[gsap.utils.wrap(0, projects.length, idx + 1)] ?? null
    }

    _getContentEls() {
        const els = [
            this.fieldLabelEls[0],
            this.titleEl,
            this.fieldLabelEls[1],
            this.descEl,
            this.fieldLabelEls[2],
            this.yearEl,
            this.fieldLabelEls[3],
            this.roleEl
        ]
        if (this.project?.url) {
            els.push(this.linkLabelEl)
        }
        return els
    }

    _cleanupAnimations() {
        this._contentAnimation?.kill()
        this._contentSplit?.revert()
        this._nextSplit?.revert()
        this._contentSplit = this._nextSplit = null
    }

    splitIn(project) {
        this._setInteractive(true)

        this.resetProgress(true)
        this._updateDOMContent(project)
        this.nextEl.style.visibility = this._nextEnabled ? 'visible' : 'hidden'
        this._cleanupAnimations()
        this.resize()

        const { split: contentSplit, tween: contentTween } = splitReveal(this._getContentEls())
        const { split: nextSplit, tween: nextTween } = splitReveal([this.nextLabelEl, this.nextOverlayEl], {
            stagger: 0
        })
        const counterTween = this.counterFx.reveal('01', { stagger: STAGGER.none })

        this._contentSplit = contentSplit
        this._nextSplit = nextSplit

        const tl = gsap.timeline()
        tl.add(contentTween, 0).add(nextTween, STAGGER.loose)
        if (counterTween) {
            tl.add(counterTween, STAGGER.loose * 2)
        }
        tl.eventCallback('onComplete', () => {
            this._flushPendingResize()
        })

        return (this._contentAnimation = tl)
    }

    splitOut() {
        this._setInteractive(false)
        this._cleanupAnimations()

        const { split: contentSplit, tween: contentTween } = splitConceal(this._getContentEls(), {
            stagger: STAGGER.none
        })
        const { split: nextSplit, tween: nextTween } = splitConceal([this.nextLabelEl, this.nextOverlayEl], {
            stagger: 0
        })
        const counterTween = this.counterFx.conceal({ stagger: STAGGER.none })

        this._contentSplit = contentSplit
        this._nextSplit = nextSplit

        const tl = gsap.timeline()
        tl.add(contentTween, 0).add(nextTween, 0)
        if (counterTween) {
            tl.add(counterTween, 0)
        }

        tl.eventCallback('onComplete', () => {
            if (this._contentSplit === contentSplit) {
                this._cleanupAnimations()
                this._updateDOMContent(null)
                this.resetProgress(true)
                this.nextEl.style.visibility = 'hidden'
            }
            this._flushPendingResize()
        })
        return (this._contentAnimation = tl)
    }

    setIndex(index) {
        const text = String(index).padStart(2, '0')
        if (text !== this.counterEl.textContent) {
            this.counterFx.revert()
            this.counterEl.textContent = text
        }
    }

    setProgress(t, instant = false) {
        const clamped = this._nextEnabled ? Math.max(0, Math.min(1, t)) : 0
        this.nextOverlayEl.style.setProperty('--next-progress', `${clamped * 100}%`)

        const wasComplete = this._nextComplete
        this._nextComplete = this._nextEnabled && clamped >= PROGRESS_COMPLETE_THRESHOLD
        this.nextEl.classList.toggle('is-complete', this._nextComplete)

        if (wasComplete && !this._nextComplete) {
            instant ? this.nextUnderline.reset() : this.nextUnderline.erase()
        }
    }

    resetProgress(instant = false) {
        this.setProgress(0, instant)
    }

    setImages(project) {
        this._revealToken++
        // aside/bottom 在手機版會被搬進這個 track 裡（見 _syncMobileLayout），清空前先請它們
        // 讓開，不然 innerHTML = '' 會把它們整個丟掉
        if (this.asideEl.parentElement === this.galleryTrackEl) {
            this.insertBefore(this.asideEl, this.galleryEl)
        }
        if (this.bottomEl.parentElement === this.galleryTrackEl) {
            this.asideEl.appendChild(this.bottomEl)
        }
        this.galleryTrackEl.innerHTML = ''
        this._heroEl = this._createImageEl({ type: 'image', src: project.thumbnail }, true, `${project.title} cover`)
        this.galleryTrackEl.appendChild(this._heroEl)

        this._images = project.medias.map((media, i) => {
            const img = this._createImageEl(media, false, `${project.title} — image ${i + 1}`)
            this.galleryTrackEl.appendChild(img)
            return img
        })

        this._galleryCount = 1 + this._images.length
        this._syncMobileLayout()
        this.galleryEl.scrollTop = 0
        this._galleryIndex = -1
    }

    /** 手機版跟圖片共用同一個 Lenis pane 一起捲動（沿用 .detail__gallery 既有的 Lenis 綁定，
     *  不另外建一顆）：.detail__aside（介紹文字）搬進 .detail__gallery-track 當第一個子元素；
     *  .detail_bottom（counter + Next Project）改搬到 track 最後面，等使用者捲完所有圖片才出現，
     *  counter 這時也沒有意義（不再跟圖片並排），交給 CSS 隱藏。
     *  桌機則兩個都搬回原位：aside 回到 <app-detail> 底下、.detail__gallery 前面，
     *  bottom 回到 aside 內部，維持原本左右並排、上下撐開的 grid/flex 佈局 */
    _syncMobileLayout() {
        const isMobile = device.breakpoint === 'mobile'
        if (isMobile) {
            if (this.asideEl.parentElement !== this.galleryTrackEl) {
                this.galleryTrackEl.insertBefore(this.asideEl, this.galleryTrackEl.firstChild)
            }
            if (this.bottomEl.parentElement !== this.galleryTrackEl) {
                this.galleryTrackEl.appendChild(this.bottomEl)
            }
        } else {
            if (this.asideEl.parentElement !== this) {
                this.insertBefore(this.asideEl, this.galleryEl)
            }
            if (this.bottomEl.parentElement !== this.asideEl) {
                this.asideEl.appendChild(this.bottomEl)
            }
        }
    }

    _createImageEl({ type, src }, isHero, alt) {
        const wrap = document.createElement('div')
        wrap.className = 'detail__gallery-wrap'
        wrap.style.opacity = 0
        if (isHero) {
            wrap.dataset.hero = 'true'
        }

        const media = type === 'video' ? this._createVideoEl(src) : this._createImgEl(src, alt)
        wrap.appendChild(media)
        return wrap
    }

    _createImgEl(src, alt) {
        const img = document.createElement('img')
        img.className = 'detail__gallery-img'
        img.src = src
        img.alt = alt || ''
        return img
    }

    /** 自動播放、loop、不顯示操作 UI 的內頁影片：muted 是自動播放的必要條件，
     *  playsInline 避免 iOS Safari 強制全螢幕播放 */
    _createVideoEl(src) {
        const video = document.createElement('video')
        video.className = 'detail__gallery-img'
        video.src = src
        video.autoplay = true
        video.loop = true
        video.muted = true
        video.setAttribute('muted', '')
        video.playsInline = true
        video.controls = false
        video.disablePictureInPicture = true
        video.preload = 'auto'
        return video
    }

    getGalleryTargetEl() {
        return this.galleryTargetEl
    }

    /** wrap 淡入前先等它自己的 <img> 真的準備好，避免動畫全程只顯示空白背景、圖片最後才彈出來。
     *  img.decode() resolve 跟 .complete 真正翻成 true 之間實測會差 1~2 幀（decode() 只保證解碼完成，
     *  不保證瀏覽器已經把這個 DOM 節點的畫面更新到位)，兩個訊號都等到才真的沒有可見的接口；
     *  設逾時保底，避免慢圖（目前多是 dummyjson 佔位圖）卡住整段轉場 */
    _whenImageReady(wrap) {
        const media = wrap.querySelector('img, video')
        const timeout = new Promise((resolve) => {
            return setTimeout(resolve, IMAGE_READY_TIMEOUT)
        })

        if (media.tagName === 'VIDEO') {
            const ready =
                media.readyState >= media.HAVE_CURRENT_DATA
                    ? Promise.resolve()
                    : new Promise((resolve) => {
                          media.addEventListener('loadeddata', resolve, { once: true })
                          media.addEventListener('error', resolve, { once: true })
                      })
            // 影片放進 track 時祖先元素常常還是 opacity:0 / visibility:hidden，declarative autoplay
            // 屬性在這種情況下實測不可靠，等它真的可見的這一刻用 JS 補一次 play()（靜音符合瀏覽器政策）
            return Promise.race([ready, timeout]).then(() => {
                return media.play().catch(() => {})
            })
        }

        const decoded = media.decode ? media.decode().catch(() => {}) : Promise.resolve()
        const loaded = media.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                  media.addEventListener('load', resolve, { once: true })
                  media.addEventListener('error', resolve, { once: true })
              })
        return Promise.race([Promise.all([decoded, loaded]), timeout])
    }

    /** Hero 是 Carousel WebGL 卡片直接飛到定位後的無縫換手，不是淡入淡出——回傳的 Promise
     *  resolve 時代表 DOM 圖片已經真的可以畫了，讓 Director 跟 carousel.hideHeroInstant() 在同一刻切換,
     *  不留可被看見的 opacity 過渡 */
    whenHeroReady() {
        const token = this._revealToken
        return this._whenImageReady(this._heroEl).then(() => {
            return token === this._revealToken
        })
    }

    showHeroInstant() {
        gsap.killTweensOf(this._heroEl)
        gsap.set(this._heroEl, { opacity: 1 })
    }

    imagesIn(includeHero = false) {
        this.enable()
        this.galleryTrackEl.style.visibility = 'visible'

        const els = includeHero ? [this._heroEl, ...this._images] : this._images
        const token = this._revealToken
        const tl = gsap.timeline()
        els.forEach((wrap, i) => {
            tl.call(
                () => {
                    this._whenImageReady(wrap).then(() => {
                        if (token !== this._revealToken) {
                            return
                        }
                        gsap.fromTo(
                            wrap,
                            { y: GALLERY_REVEAL_OFFSET, opacity: 0 },
                            { y: 0, opacity: 1, duration: DURATION.spread, ease: EASE.reveal }
                        )
                    })
                },
                [],
                i * STAGGER.tight
            )
        })
        if (!els.length) {
            tl.to({}, { duration: 0.01 })
        }
        return tl
    }

    imagesOut() {
        this.disable()
        const tl = gsap.timeline()
        ;[this._heroEl, ...this._images].forEach((img, i) => {
            tl.to(img, { opacity: 0, duration: DURATION.spread, ease: EASE.out }, i * STAGGER.tight)
        })
        tl.eventCallback('onComplete', () => {
            this.galleryTrackEl.style.visibility = 'hidden'
            // .detail__gallery-target 是 .detail__gallery 的子元素，而 .detail__gallery 是真的在捲動的容器，
            // 沒歸零的話下次 Carousel.leaveToDetail() 量測落地座標時，會連著這次殘留的 scrollTop 一起偏移
            this.galleryEl.scrollTop = 0
        })
        return tl
    }

    enable() {
        this._lenis?.start()
    }

    disable() {
        this._lenis?.stop()
    }

    update() {
        if (!this._galleryCount) {
            return
        }
        this._lenis.raf(this._director.studio.time.current)

        const progress = this._galleryCount > 1 ? this._imageProgress() : 1
        const idx = Math.round(progress * (this._galleryCount - 1))
        if (idx !== this._galleryIndex) {
            this._galleryIndex = idx
            this.setIndex(idx + 1)
        }

        const nextProgress = device.breakpoint === 'mobile' ? 1 : progress
        this.setProgress(nextProgress)
    }

    /** 手機版 aside 佔掉了 track 開頭一段高度、bottom（counter + Next Project 本人）又佔掉結尾一段
     *  （見 _syncMobileLayout），兩段都不算「翻頁進度」，否則計數器跟 Next Project 的完成門檻會
     *  提早或延後觸發——扣掉這兩段的高度，只算真正圖片區間的捲動比例。桌機因為兩者都不在 track 裡，
     *  offset 恆為 0，行為等同原本直接讀 this._lenis.progress */
    _imageProgress() {
        const asideOffset = this.asideEl.parentElement === this.galleryTrackEl ? this.asideEl.offsetHeight : 0
        const bottomOffset = this.bottomEl.parentElement === this.galleryTrackEl ? this.bottomEl.offsetHeight : 0
        const limit = this._lenis.limit - asideOffset - bottomOffset
        if (limit <= 0) {
            return 1
        }
        const scrolled = this._lenis.animatedScroll - asideOffset
        if (limit - scrolled <= COMPLETE_EPSILON_PX) {
            return 1
        }
        return Math.min(1, Math.max(0, scrolled / limit))
    }

    resize() {
        this._syncMobileLayout()
        this._lenis?.resize()

        if (this._contentAnimation?.isActive()) {
            this._pendingResize = true
            return
        }
        this._resplitStatic()
    }

    _flushPendingResize() {
        if (this._pendingResize) {
            this._resplitStatic()
        }
    }

    _resplitStatic() {
        this._pendingResize = false

        if (!this._contentSplit && !this._nextSplit) {
            return
        }

        this._contentSplit?.revert()
        this._nextSplit?.revert()
        this._contentSplit = resplitLines(this._getContentEls())
        this._nextSplit = resplitLines([this.nextLabelEl, this.nextOverlayEl])
    }

    disconnectedCallback() {
        this._cleanupAnimations()
        this.counterFx.revert()
        this._lenis?.destroy()
    }
}

if (!customElements.get('app-detail')) {
    customElements.define('app-detail', AppDetail)
}

export const detail = document.querySelector('app-detail')
